import React, { useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import PrintLabel from './PrintLabel';
import { StorageData } from '../types/storage';
import { storageApi } from '../utils/api';
import { formatPlateForDisplay } from '../utils/plateFormatter';

interface Props {
  data: StorageData[];
  onOpenDetail: (id: string) => void;
  onOpenEdit: (id: string) => void;
  onRefresh: () => void;
}

const StorageTable: React.FC<Props> = ({ data, onOpenDetail, onOpenEdit, onRefresh }) => {
  const navigate = useNavigate();
  const [printData, setPrintData] = useState<StorageData | null>(null);
  const [sortField, setSortField] = useState<keyof StorageData>('musteri');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleEdit = (id: string) => {
    navigate(`/dashboard/storage/${id}`);
  };

  const handleSort = (field: keyof StorageData) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    
    if (aValue === undefined || bValue === undefined) {
      return 0;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    }

    // Tarih karşılaştırması için
    if (sortField === 'tarih') {
      const dateA = new Date(aValue as string).getTime();
      const dateB = new Date(bValue as string).getTime();
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    }

    return 0;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Bu kaydı silmek istediğinizden emin misiniz?')) {
      try {
        await storageApi.delete(id);
        onRefresh();
      } catch (error) {
        console.error('Silme hatası:', error);
      }
    }
  };

  return (
    <div className="table-container">
      {printData && (
        <div style={{ display: 'none' }}>
          <PrintLabel 
            data={printData} 
            onPrintComplete={() => setPrintData(null)} 
          />
        </div>
      )}
      <div className="table-scroll-hint d-md-none">
        Tabloyu görmek için sağa/sola kaydırın
      </div>
      <div className="table-responsive">
        <table className="table table-sm">
          <thead>
            <tr style={{ fontSize: '0.85rem' }}>
              <th onClick={() => handleSort('musteri')} className="sortable" style={{ width: '12%' }}>
                Müşteri {sortField === 'musteri' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('no')} className="sortable" style={{ width: '8%' }}>
                No {sortField === 'no' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('tarih')} className="sortable" style={{ width: '10%' }}>
                Tarih {sortField === 'tarih' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('ebat')} className="sortable" style={{ width: '10%' }}>
                Ebat {sortField === 'ebat' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('marka')} className="sortable" style={{ width: '10%' }}>
                Marka {sortField === 'marka' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('adet')} className="sortable" style={{ width: '5%' }}>
                Adet {sortField === 'adet' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('plaka')} className="sortable" style={{ width: '10%' }}>
                Plaka {sortField === 'plaka' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('not')} className="sortable" style={{ width: '15%' }}>
                Not {sortField === 'not' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ width: '100px', minWidth: '100px' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item) => (
              <tr key={item._id} style={{ fontSize: '0.85rem' }}>
                <td onClick={() => onOpenDetail(item._id)}>{item.musteri}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.no ? item.no.toString().padStart(6, '0') : '-'}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.tarih ? format(new Date(item.tarih), 'dd.MM.yyyy') : '-'}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.ebat}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.marka}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.adet}</td>
                <td onClick={() => onOpenDetail(item._id)}>{formatPlateForDisplay(item.plaka)}</td>
                <td onClick={() => onOpenDetail(item._id)}>{item.not}</td>
                <td style={{ padding: '4px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '90px' }}>
                    <button 
                      className="btn btn-secondary btn-sm"
                      style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                      onClick={() => onOpenEdit(item._id)}
                    >
                      Düzenle
                    </button>
                    <button 
                      className="btn btn-success btn-sm"
                      style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                      onClick={() => setPrintData(item)}
                    >
                      Yazdır
                    </button>
                    <button 
                      className="btn btn-danger btn-sm"
                      style={{ width: '100%', padding: '2px 8px', fontSize: '0.8rem' }}
                      onClick={() => handleDelete(item._id)}
                    >
                      Sil
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StorageTable; 